import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useParams, useNavigate, Link } from 'react-router-dom';

import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import VideoPlayer from './partial/stream/VideoPlayer.jsx';
import ChatSidebar from './partial/stream/ChatSidebar.jsx';
import ChatHighlights from './partial/stream/ChatHighlights.jsx';
import NowPlayingCard from './partial/stream/NowPlayingCard.jsx';
import SetlistRail, { canBoostEntry } from './partial/stream/SetlistRail.jsx';
import PerformerCard from './partial/stream/PerformerCard.jsx';
import StreamActionBar from './partial/stream/StreamActionBar.jsx';
import BoostDesk, { boostPayError } from './partial/stream/BoostDesk.jsx';
import SaveCardBanner from './partial/stream/SaveCardBanner.jsx';
import FollowButton from './partial/FollowButton.jsx';
import StreamLyricsPanel from './partial/stream/StreamLyricsPanel.jsx';
import TipPanel from './partial/stream/TipPanel.jsx';
import YourSongCard from './partial/stream/YourSongCard.jsx';
import LiveMomentCard from './partial/stream/LiveMomentCard.jsx';
import RatingCard from './partial/stream/RatingCard.jsx';
import JustPlayedCard from './partial/stream/JustPlayedCard.jsx';
import SongCratePanel from './partial/stream/SongCratePanel.jsx';
import SearchResultsPanel from './partial/stream/SearchResultsPanel.jsx';
import SongPagePanel from './partial/stream/SongPagePanel.jsx';
import RequestCheckPanel from './partial/stream/RequestCheckPanel.jsx';
import { useRequestPayment } from '../hooks/useRequestPayment.js';
import { useSkinPreview } from '../hooks/useSkinPreview.js';
import { playingSongKey } from '../_helpers/cardSkin.js';
import { useSkinTryOn } from '../hooks/useSkinTryOn.js';
import { useSkinPurchase } from '../hooks/useSkinPurchase.js';
import { fetchSkinOffer } from '../_helpers/skinTryOn.js';
import SkinTryOnPanel, { skinPurchaseError } from './partial/stream/SkinTryOnPanel.jsx';
import SkinShelfPanel from './partial/stream/SkinShelfPanel.jsx';
import { STREAM_SEARCH_MIN_CHARS } from '../hooks/useStreamSearch.js';
import SongRecommendationNotification from './partial/stream/SongRecommendationNotification.jsx';

import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { isBandMember, getCurrentUserId, extractTwitchChannel, canBandTakeTips } from '../_helpers/bandUtils.js';
import { isSignedIn, rememberReturnPath } from '../_helpers/safeRedirect.js';
import { useStreamStatus } from '../hooks/useStreamStatus.js';
import { useStreamBootstrap } from '../hooks/useStreamBootstrap.js';
import { useBoostPayment } from '../hooks/useBoostPayment.js';
import { useMyRequests } from '../hooks/useMyRequests.js';
import { useLiveMoment } from '../hooks/useLiveMoment.js';
import { useSongLyrics } from '../hooks/useSongLyrics.js';
import { useFeatureFlag } from '../hooks/useFeatureFlag.js';
import { useSongRecommendation } from '../hooks/useSongRecommendation.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { FxContext } from '../contexts/FxContext.jsx';
import { useFxStage } from '../hooks/useFxStage.js';
import { useFxVotes } from '../hooks/useFxVotes.js';
import { useFxBoosts } from '../hooks/useFxBoosts.js';
import { useFxAfterglow } from '../hooks/useFxAfterglow.js';
import { useReduceEffects } from '../hooks/useReduceEffects.js';
import { useVibeSetPicker } from '../hooks/useVibeSetPicker.js';
import VibeSetPicker from './partial/stream/VibeSetPicker.jsx';
import { BroadcastSafeSlot, useBroadcastSurface } from '../contexts/BroadcastSurface.jsx';
import config from '../config.js';

import '../styles/StreamPage.css';

// Re-exported so existing imports keep working; the implementation lives in bandUtils.
export { extractTwitchChannel };

/**
 * The listener stream page (mockup 01 of the Alpha Design Selects, ENG-667).
 *
 * Its start state (band header, crate preview, now playing, setlist rail and
 * chat highlights) comes from one call, `GET /public/bands/{id}/stream/
 * bootstrap` (ENG-668). The band's stream-status socket keeps it current: a
 * `queue_update` or a live flip re-reads the bootstrap, `viewer_count_update`
 * moves the watching count, and `stream_highlights_update` replaces the
 * pinned chat rows in place. Band detail (`GET /bands/{id}`) is still read for
 * what the bootstrap leaves out on purpose: membership, the Twitch channel and
 * the follow state.
 */
const StreamPage = () => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const { t, format } = useLocale();
  // Nothing displays over the broadcast: app-level overlays stay off this
  // page (even while it loads) and render in the side column's slot instead.
  useBroadcastSurface();

  const [band, setBand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Catalog key of the load error, translated at render so a locale switch applies.
  const [error, setError] = useState(null);
  // The tip panel (mockup 08, ENG-681) takes the performer card's place while open.
  const [tipOpen, setTipOpen] = useState(false);
  // The band's one-tap tip amounts, from the public band info (ENG-682).
  const [tipPresetsCents, setTipPresetsCents] = useState([]);
  // From `/public/bands/{id}`: the band detail has no payment fields, so it
  // can't say whether the band takes tips (ENG-496).
  const [canTakeTips, setCanTakeTips] = useState(false);
  // From `/public/bands/{id}`: whether the band takes free requests (ENG-664).
  const [freeRequestsEnabled, setFreeRequestsEnabled] = useState(false);
  const [nowPlayingCollapsed, setNowPlayingCollapsed] = useState(false);
  // Boost pick mode (mockup 05, ENG-675): the setlist spans the page and each
  // boostable song offers "↑ Boost"; the chosen entry opens the boost desk.
  const [boostPicking, setBoostPicking] = useState(false);
  const [boostEntryId, setBoostEntryId] = useState(null);
  const closeBoostPick = useCallback(() => {
    setBoostPicking(false);
    setBoostEntryId(null);
  }, []);

  const closeTip = useCallback(() => setTipOpen(false), []);

  const bootstrap = useStreamBootstrap(bandid);
  const { refresh: refreshBootstrap, setHighlights } = bootstrap;

  // A paid boost (mockup 07, ENG-679): what this viewer has boosted each
  // song by (the "YOU +$5" tag, kept for the tab's session) and the chat
  // lines only they see (a boost that passed nobody gets no public line).
  const boostsKey = `stream-boosts:${bandid}`;
  const [yourBoosts, setYourBoosts] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(boostsKey) || '{}') || {};
    } catch (_e) {
      return {};
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(boostsKey, JSON.stringify(yourBoosts));
    } catch (_e) {
      // Storage may be unavailable; the tag lasts until reload then.
    }
  }, [boostsKey, yourBoosts]);
  const [ownChatLines, setOwnChatLines] = useState([]);
  const queueRef = useRef([]);
  const handleBoosted = useCallback((result) => {
    setYourBoosts((prev) => ({ ...prev, [result.entryId]: (prev[result.entryId] || 0) + result.amountCents }));
    if (!result.passedCount) {
      const entry = queueRef.current.find((e) => e.entryId === result.entryId);
      setOwnChatLines((prev) => [...prev, {
        id: `boost-${result.tipId}`,
        timestamp: new Date().toISOString(),
        still: true,
        boost: { entryId: result.entryId, songTitle: entry?.title ?? '', amountCents: result.amountCents, spot: result.spot, passedTitle: '', passedCount: 0 },
      }]);
    }
    setBoostPicking(false);
    setBoostEntryId(null);
    refreshBootstrap();
    refreshMyRequestsRef.current();
  }, [refreshBootstrap]);
  const boostPayment = useBoostPayment(bandid, {
    onBoosted: handleBoosted,
    returnPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
  const resetBoostPayment = boostPayment.reset;
  const liveMomentFrameRef = useRef(() => {});
  const onLiveMoment = useCallback((data) => liveMomentFrameRef.current(data), []);
  // "Your song just played" (mockup 15, ENG-695): the private song_played
  // frame, and the listener's memories for the Premium card.
  const [justPlayed, setJustPlayed] = useState(null);
  const [memorySummary, setMemorySummary] = useState(null);
  const onSongPlayed = useCallback((frame) => {
    setJustPlayed(frame);
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me/memories/summary`)
      .then(setMemorySummary)
      .catch(() => setMemorySummary(null));
  }, []);
  const refreshMyRequestsRef = useRef(() => {});
  const applyPositionsRef = useRef(null);
  // fx_vote / fx_energy go to useFxVotes, which needs the FX stage below.
  const fxVoteFramesRef = useRef({});

  // The watch page is the only place a session counts as attending the show,
  // so it alone opts its status socket in to attendance tracking (ENG-338).
  // The opt-in is fixed for the whole mount: flipping it would tear the socket
  // down and reset status (ENG-337).
  const {
    isLive,
    viewerCount,
    embedUrl,
  } = useStreamStatus(bandid, {
    trackAttendance: true,
    onQueueUpdate: refreshBootstrap,
    onHighlightsUpdate: setHighlights,
    onBoostSettled: boostPayment.settled,
    onLiveMoment,
    onSongPlayed,
    onSongStage: (frame) => {
      refreshBootstrap();
      if (frame?.type === 'song_started') fxVoteFramesRef.current.songStarted?.(frame);
    },
    onRequestPositions: (frame) => applyPositionsRef.current?.(frame),
    onFxVote: (frame) => fxVoteFramesRef.current.vote?.(frame),
    onFxEnergy: (frame) => fxVoteFramesRef.current.energy?.(frame),
    onFxBoost: (frame) => fxVoteFramesRef.current.boost?.(frame),
  });
  /*
   * SongShake FX on the queue (ENG-804): one engine stage on the
   * now-playing + setlist strip while the show is live, never drawing above
   * the stream player's bottom edge. The queue rail registers its cards;
   * later FX work plays effects on them through FxContext (`useFx()`).
   */
  const stageMainRef = useRef(null);
  const getFxAvoid = useCallback(() => stageMainRef.current?.querySelector('.video-player') ?? null, []);
  // Reduced effects (ENG-810, ENG-836): no flash, lightning, flicker or shake while the
  // account's Photosensitivity mode is on, the account is not loaded yet, the first-login
  // warning is open, or the device asks for reduced motion.
  const reduceEffects = useReduceEffects();
  const fx = useFxStage({ enabled: isLive, getAvoid: getFxAvoid, reduceEffects });
  // The vibe set picker (ENG-812): signed in only; signed out the stage keeps the default vibe.
  const vibePicker = useVibeSetPicker({ fx, enabled: fx.status !== 'off' && isSignedIn() });
  const vibePickerRef = useRef(vibePicker);
  vibePickerRef.current = vibePicker;
  // The live moment on the show (trivia, ENG-690/691).
  const liveMoment = useLiveMoment(bandid, { enabled: isLive });
  liveMomentFrameRef.current = liveMoment.frame;
  const { recommendation, dismissRecommendation } = useSongRecommendation({ bandId: bandid });
  // The listener's own waiting requests (the YOUR SONG card, ENG-683/685/687).
  const myRequests = useMyRequests(bandid, { enabled: isLive && isSignedIn(), queueKey: bootstrap.data?.queue });
  refreshMyRequestsRef.current = myRequests.refresh;
  applyPositionsRef.current = myRequests.applyPositions;

  /*
   * A request from the song page (mockups 22-28, ENG-709 on): the song page,
   * then the check step, in the right column over the crate or the search,
   * which stay as they were for "Back to the crate". Once the request is in
   * the queue (ENG-715) the panels close and the chat comes back with the
   * YOUR SONG card. The payment hook lives here so a return from Stripe
   * Checkout finishes too.
   */
  const [requestFlow, setRequestFlow] = useState(null);
  const handleRequested = useCallback(() => {
    setRequestFlow(null);
    setCrate(null);
    setSearchTerm('');
    refreshMyRequestsRef.current?.();
    refreshBootstrap();
  }, [refreshBootstrap]);
  const requestPayment = useRequestPayment(bandid, {
    onRequested: handleRequested,
    returnPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
  const openSongPage = useCallback((song) => {
    requestPayment.reset();
    setRequestFlow({ step: 'song', songId: song.id, song, choice: null });
  }, [requestPayment]);

  // A live flip changes what the bootstrap serves (offline: no queue, no
  // highlights), so re-read it when the socket reports one.
  const lastLiveRef = useRef(isLive);
  useEffect(() => {
    if (lastLiveRef.current !== isLive) {
      lastLiveRef.current = isLive;
      refreshBootstrap();
    }
  }, [isLive, refreshBootstrap]);

  const fetchWrapperRef = useRef(useFetchWrapper());
  const isMember = useRef(false);

  useEffect(() => {
    const fetchBand = async () => {
      try {
        setIsLoading(true);
        const bandData = await fetchWrapperRef.current.get(
          `${config.apiBaseUrl}/bands/${bandid}`
        );
        const normalizedBand = {
          ...bandData,
          ID: bandData.id ?? bandData.ID,
          Name: bandData.name ?? bandData.Name,
          Description: bandData.description ?? bandData.Description,
          ProfileImageURL: bandData.profile_image_url ?? bandData.ProfileImageURL,
          SocialMediaLinks: bandData.social_media_links ?? bandData.SocialMediaLinks,
          Members: bandData.members ?? bandData.Members ?? [],
        };
        setBand(normalizedBand);
        isMember.current = isBandMember(normalizedBand, getCurrentUserId());
      } catch (err) {
        console.error('Error fetching band data:', err);
        setError('stream.unavailable.loadFailed');
      } finally {
        setIsLoading(false);
      }
    };

    if (bandid) {
      fetchBand();
    }
  }, [bandid]);

  useEffect(() => {
    if (!bandid) return undefined;
    let cancelled = false;
    setCanTakeTips(false);
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/bands/${bandid}`)
      .then((info) => {
        if (cancelled) return;
        setCanTakeTips(canBandTakeTips(info));
        setTipPresetsCents(Array.isArray(info?.tipPresetsCents) ? info.tipPresetsCents.filter((c) => Number.isInteger(c) && c > 0) : []);
        setFreeRequestsEnabled(info?.freeRequestsEnabled === true);
      })
      .catch(() => { if (!cancelled) setCanTakeTips(false); });
    return () => { cancelled = true; };
  }, [bandid]);

  // Escape leaves pick mode; so does the show ending.
  useEffect(() => {
    if (!boostPicking) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeBoostPick();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [boostPicking, closeBoostPick]);
  useEffect(() => {
    if (!isLive) closeBoostPick();
  }, [isLive, closeBoostPick]);

  const nowPlaying = isLive ? bootstrap.data?.nowPlaying ?? null : null;
  // Between songs the card shows what plays next (mockup 17, ENG-699).
  const upNext = isLive && !nowPlaying ? bootstrap.data?.upNext ?? null : null;
  /*
   * "See what they see" (mockup 35, ENG-735): another listener's now-playing
   * card skin on this viewer's card only. It ends on "Back to mine" or when
   * the playing song changes.
   */
  const skinPreview = useSkinPreview(playingSongKey(nowPlaying));
  /*
   * "Try it on" (mockup 36, ENG-737): the skin a listener wears, on this
   * viewer only, until "Take it off" or the next song; it replaces a "See
   * what they see" preview and the other way round. "Get it" buys it
   * (ENG-738) with the saved card or on Stripe Checkout, which comes back
   * here; a confirmed purchase is owned and equipped, so the try-on comes
   * off and the chat says so.
   */
  const skinTryOn = useSkinTryOn(playingSongKey(nowPlaying));
  const skinTryOnRef = useRef(skinTryOn);
  skinTryOnRef.current = skinTryOn;
  const [skinNotice, setSkinNotice] = useState(null);
  // Bumped by a confirmed purchase, so an open skin shelf reads again (ENG-816).
  const [shelfRefresh, setShelfRefresh] = useState(0);
  const handleSkinPurchased = useCallback((result) => {
    setShelfRefresh((n) => n + 1);
    // A purchase equips what was bought; a vibe set switches the queue's look now (ENG-812).
    vibePickerRef.current.sync().catch(() => {});
    const on = skinTryOnRef.current;
    const offer = on.offer.status === 'ready' ? on.offer.offer : null;
    const sameSkin = on.tryOn?.skin.id === result.skinId;
    setSkinNotice({
      name: sameSkin ? on.tryOn.skin.name : '',
      editionNumber: result.editionNumber,
      editionSize: sameSkin ? offer?.editionSize ?? on.tryOn.skin.editionSize : null,
    });
    on.takeOff();
    // Back from Checkout nothing is on, so read the skin's name and run size.
    if (!sameSkin && result.skinId) {
      fetchSkinOffer(result.skinId)
        .then((found) => setSkinNotice((notice) => (notice ? { ...notice, name: found.skin.name, editionSize: found.editionSize } : notice)))
        .catch(() => {});
    }
  }, []);
  // The price moved since the panel opened (ENG-825/827): read the offer again, so the next tap sends the new price.
  const handleSkinPriceChanged = useCallback(({ skinId }) => {
    skinTryOnRef.current.refresh(skinId);
    // The shelf's locked FX items carry prices too (ENG-816).
    setShelfRefresh((n) => n + 1);
  }, []);
  // Sold out under the listener (409 skin_sold_out, ENG-833): the same re-read, so the try-on and shelf say Sold out.
  const skinPurchase = useSkinPurchase({
    onPurchased: handleSkinPurchased,
    onPriceChanged: handleSkinPriceChanged,
    onSoldOut: handleSkinPriceChanged,
  });
  const resetSkinPurchase = skinPurchase.reset;
  const { revert: revertPreview, apply: applyPreview } = skinPreview;
  const tryItOn = useCallback((card) => {
    if (!card?.wearing?.skin) return;
    revertPreview();
    resetSkinPurchase();
    setSkinNotice(null);
    skinTryOnRef.current.start(card.wearing.skin);
  }, [revertPreview, resetSkinPurchase]);
  /*
   * The skin shelf (mockup 37, ENG-739): "See all" on this viewer's own
   * profile card opens it in place of the chat and the action bar; the
   * performer card stays. Holds the card it was opened from, for the header.
   */
  const [shelfCard, setShelfCard] = useState(null);
  const openShelf = useCallback((card) => setShelfCard(card ?? {}), []);
  const closeShelf = useCallback(() => setShelfCard(null), []);
  const seeWhatTheySee = useCallback((card) => {
    skinTryOnRef.current.takeOff();
    return applyPreview(card);
  }, [applyPreview]);

  /*
   * Sing-along lyrics for whatever just started (ENG-415), keyed on the
   * playing song, only while the show is on: a stream that ended can leave an
   * entry marked playing, and a lookup nobody watches still costs a metered
   * Musixmatch call. Also only while the operator's lyrics flag is on
   * (ENG-783): it fails closed, so nothing is asked for or shown until
   * `GET /public/features` answers `lyrics: true`.
   */
  const { enabled: lyricsEnabled } = useFeatureFlag('lyrics');
  const lyrics = useSongLyrics(nowPlaying?.songId, { enabled: isLive && lyricsEnabled });
  // A "Finish the lyric" moment is a listener lyrics surface too; with the
  // flag off it is skipped as if there were no moment. Trivia and rating
  // moments are unaffected.
  const visibleMoment =
    liveMoment.moment && !(liveMoment.moment.kind === 'lyric' && !lyricsEnabled) ? liveMoment.moment : null;

  // The song crate (mockup 16, ENG-697) takes the whole right column while
  // open; with switchEntryId, the song picked replaces that request's.
  const [crate, setCrate] = useState(null);
  const openCrate = useCallback(() => setCrate({ switchEntryId: null }), []);
  const closeCrate = useCallback(() => setCrate(null), []);
  // The header search (mockup 18, ENG-701): a query long enough to search
  // shows its results in the right column, over the crate if that is open.
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    searchInputRef.current?.focus();
  }, []);
  const searching = [...searchTerm.trim()].length >= STREAM_SEARCH_MIN_CHARS;
  const goSignIn = useCallback(() => {
    rememberReturnPath();
    navigate('/login');
  }, [navigate]);
  /*
   * Get it on a locked vibe set or boost animation on the shelf (ENG-816):
   * bought through the same useSkinPurchase as a skin, never a call of its own.
   */
  // Wear / Take off a vibe set on the shelf, saved: the queue switches to it now (ENG-812).
  const shelfEquipChange = useCallback((skin, worn) => {
    if (skin?.type !== 'vibe_set') return;
    vibePickerRef.current.applyWorn(worn ? skin.fxPackageId : null).catch(() => {});
  }, []);
  const buyFromShelf = useCallback((item) => {
    if (!item?.skin?.id) return;
    if (!isSignedIn()) {
      goSignIn();
      return;
    }
    skinTryOnRef.current.takeOff();
    setSkinNotice(null);
    skinPurchase.buy(item.skin.id, { priceCents: item.priceCents, name: item.skin.name, onSale: Boolean(item.sale) });
  }, [goSignIn, skinPurchase]);

  // Upvotes and crowd energy (ENG-806): tapping a song's art votes. Voting
  // needs a session (Tim's decision): signed out, the cards offer no vote
  // target and a tap does nothing. Everyone sees the builds and other
  // people's votes.
  /*
   * Boost playback (ENG-808): every viewer sees a confirmed boost's
   * animation (the booster's package) on the song's card, and the card slides
   * to its new spot as part of it. While boosts play the rail's order is held
   * here (`fxBoosts.entries`) and the boost's chat line waits for its end.
   */
  const fxBoosts = useFxBoosts({ fx, queue: isLive ? bootstrap.data?.queue ?? [] : [] });
  const fxVotes = useFxVotes({
    bandId: bandid,
    fx,
    entries: fxBoosts.entries,
  });
  // Afterglow and the #1 lock (ENG-809): the #1 and up-next cards wear the
  // locked look, and a song's afterglow and build clear when it starts playing.
  const fxAfterglow = useFxAfterglow({ fx, entries: fxBoosts.entries });
  fxVoteFramesRef.current = {
    vote: fxVotes.onVoteFrame,
    energy: fxVotes.onEnergyFrame,
    boost: fxBoosts.onBoostFrame,
    songStarted: fxAfterglow.onSongStarted,
  };

  // Request and Boost need a session; signed out, they go to sign-in and come
  // back here (ENG-503). Tips do not: the public checkout takes them from
  // anyone (ENG-468).
  const handleAction = useCallback((kind) => {
    if (kind === 'tip') {
      setTipOpen((open) => !open);
      return;
    }
    if (!isSignedIn()) {
      rememberReturnPath();
      navigate('/login');
      return;
    }
    if (kind === 'request') openCrate();
    if (kind === 'boost') {
      resetBoostPayment();
      setBoostEntryId(null);
      setBoostPicking(true);
    }
  }, [navigate, openCrate, resetBoostPayment]);

  if (isLoading) {
    return (
      <div className="stream-page">
        <StreamPageHeader />
        <div className="stream-page-loading" data-testid="stream-page-loading">
          <div className="loading-content">
            <h2>{t('stream.loading.title')}</h2>
            <p>{t('stream.loading.description')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !band) {
    return (
      <div className="stream-page">
        <StreamPageHeader />
        <div className="stream-page-error" data-testid="stream-page-error">
          <div className="error-content">
            <h2>{t('stream.unavailable.title')}</h2>
            <p>{t(error || 'stream.unavailable.couldNotLoad')}</p>
            <button onClick={() => navigate(`/performers/${bandid}`)} className="back-button">
              <FaArrowLeft className="rtl-mirror" /> {t('stream.unavailable.backToBand')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const twitchChannel = extractTwitchChannel(band);
  const headerBand = bootstrap.data
    ? { ...bootstrap.data.band, isLive, viewerCount }
    : { name: band.Name, avatarUrl: band.ProfileImageURL, isLive, viewerCount, requestsOpen: false };
  const liveQueue = isLive ? bootstrap.data?.queue ?? [] : [];
  queueRef.current = liveQueue;
  const yourEntryIds = myRequests.requests.map((request) => request.entryId);
  const switchSong = (request) => setCrate({ switchEntryId: request.entryId });
  // Members see their own stream; tipping or boosting themselves is not a
  // thing (STREAM-23). Tips and boosts are paid, so they need a show that is
  // on and a band that takes payments; a boost also needs a song it can move.
  const canPay = canTakeTips && !isMember.current && isLive;
  // The song on the boost desk, while it can still be boosted; when it plays,
  // locks as next up or leaves the queue, the strip falls back to pick mode.
  // Never #1 or the locked up-next song: the backend refuses both (ENG-807/808).
  const boostEntry = liveQueue.find((entry) => entry.entryId === boostEntryId && canBoostEntry(entry)) ?? null;
  const anyBoostable = liveQueue.some(canBoostEntry);
  const disabledActions = [
    ...(canPay ? [] : ['tip']),
    ...(canPay && anyBoostable ? [] : ['boost']),
    ...(isLive && headerBand.requestsOpen ? [] : ['request']),
  ];
  // Why Tip and Boost are off (ENG-772), most lasting reason first: a member
  // can never pay their own band, a band without payments can't take them
  // live or not, and an offline show opens later. Boost also needs a song it
  // can move. A reason is set exactly when the action is in disabledActions.
  const payReason = isMember.current ? 'ownBand' : !canTakeTips ? 'noPayments' : !isLive ? 'offline' : null;
  const boostReason = payReason ?? (anyBoostable ? null : 'noBoostable');
  const disabledReasons = {
    ...(payReason ? { tip: payReason } : {}),
    ...(boostReason ? { boost: boostReason } : {}),
  };

  return (
    <div className="stream-page">
      <StreamPageHeader searchTerm={searchTerm} onSearchChange={setSearchTerm} searchInputRef={searchInputRef} />

      <div className="stream-page-wrapper" data-testid="stream-page">
        <main className="stream-stage" ref={stageMainRef}>
          <VideoPlayer
            bandId={bandid}
            twitchChannel={twitchChannel}
            isLoading={false}
            error={!isLive ? t('stream.player.offline') : null}
            streamUrl={isLive ? embedUrl : null}
          />

          {!boostPicking && (
            <FxContext.Provider value={fx.status === 'off' ? null : fx}>
            <div
              ref={fx.containerRef}
              className={`stream-stage-strip${nowPlayingCollapsed ? ' stream-stage-strip--collapsed' : ''}`}
              data-testid="stream-stage-strip"
            >
              <NowPlayingCard
                song={nowPlaying ?? upNext}
                upNext={!nowPlaying && Boolean(upNext)}
                collapsed={nowPlayingCollapsed}
                onToggleCollapsed={() => setNowPlayingCollapsed((v) => !v)}
                skin={skinTryOn.tryOn?.look ?? skinPreview.preview?.look ?? null}
                preview={skinPreview.preview ? { name: skinPreview.preview.name, onBack: skinPreview.revert } : null}
                tryOn={skinTryOn.tryOn?.look ? { name: skinTryOn.tryOn.skin.name } : null}
              />
              <SetlistRail
                entries={fxBoosts.entries}
                glide={!fxBoosts.moving}
                loading={bootstrap.loading}
                error={bootstrap.error}
                yourBoosts={yourBoosts}
                yourEntryIds={yourEntryIds}
                onVoteEntry={fx.status !== 'off' && isSignedIn() ? fxVotes.tap : undefined}
                voteHint={fxVotes.hint}
              />
            </div>
            </FxContext.Provider>
          )}
          {!boostPicking && fx.status !== 'off' && vibePicker.enabled && (
            <div className="stream-fx-bar">
              <VibeSetPicker picker={vibePicker} />
            </div>
          )}
        </main>

        <aside className={`stream-side${searching && !requestFlow ? ' stream-side--search' : ''}`}>
          {/* App-level content that would otherwise cover the player (the
              photosensitivity warning, ENG-836) renders here, beside it. */}
          <BroadcastSafeSlot />
          {requestFlow?.step === 'song' && (
            <SongPagePanel
              bandId={bandid}
              songId={requestFlow.songId}
              bandName={headerBand.name}
              freeRequestsEnabled={freeRequestsEnabled}
              queueKey={bootstrap.data?.queue}
              initialChoice={requestFlow.choice}
              onBack={() => setRequestFlow(null)}
              onClose={() => { setRequestFlow(null); setCrate(null); }}
              onNext={(choice) => setRequestFlow((flow) => ({ ...flow, step: 'check', choice }))}
              onSignIn={goSignIn}
            />
          )}
          {requestFlow?.step === 'check' && (
            <RequestCheckPanel
              bandId={bandid}
              song={{ id: requestFlow.songId, title: requestFlow.song?.title ?? '', artist: requestFlow.song?.artist ?? '' }}
              choice={requestFlow.choice}
              queueKey={bootstrap.data?.queue}
              payment={requestPayment}
              onChangeAmount={() => { requestPayment.reset(); setRequestFlow((flow) => ({ ...flow, step: 'song' })); }}
              onClose={() => { setRequestFlow(null); setCrate(null); }}
            />
          )}
          {/* Under the song page, the crate or search keeps its place. */}
          <div className="stream-side-column" hidden={Boolean(requestFlow)}>
            {searching && (
              <SearchResultsPanel
                bandId={bandid}
                term={searchTerm}
                onClear={clearSearch}
                onDone={myRequests.refresh}
                onSignIn={goSignIn}
                onOpenSong={openSongPage}
              />
            )}
            {crate && !searching && (
              <SongCratePanel
                bandId={bandid}
                switchEntryId={crate.switchEntryId}
                playingSongId={nowPlaying?.songId ?? null}
                onClose={closeCrate}
                onDone={myRequests.refresh}
                onSignIn={goSignIn}
                onOpenSong={openSongPage}
              />
            )}
          </div>
          {/* Hidden, not unmounted, under the crate or search: the chat keeps its
              socket, scroll and draft. */}
          <div className="stream-side-column" hidden={Boolean(crate) || searching || Boolean(requestFlow)}>
          {tipOpen && canPay ? (
            <TipPanel bandId={bandid} presetsCents={tipPresetsCents} onClose={closeTip} />
          ) : liveMoment.moment?.kind === 'rating' ? (
            <RatingCard
              moment={liveMoment.moment}
              votes={liveMoment.votes}
              result={liveMoment.result}
              answered={liveMoment.answered}
              offsetMs={liveMoment.offsetMs}
              onRate={(question, liked) => {
                if (!isSignedIn()) {
                  rememberReturnPath();
                  navigate('/login');
                  return;
                }
                liveMoment.rate(question, liked);
              }}
              onDismiss={liveMoment.dismiss}
            />
          ) : visibleMoment ? (
            <LiveMomentCard
              moment={visibleMoment}
              chosen={liveMoment.chosen}
              result={liveMoment.result}
              answered={liveMoment.answered}
              error={liveMoment.error}
              offsetMs={liveMoment.offsetMs}
              onAnswer={(option) => {
                if (!isSignedIn()) {
                  rememberReturnPath();
                  navigate('/login');
                  return;
                }
                liveMoment.answer(option);
              }}
              onDismiss={liveMoment.dismiss}
            />
          ) : justPlayed ? (
            <JustPlayedCard
              played={justPlayed}
              summary={memorySummary}
              // The subscription page is ENG-756; until it ships, Premium is on the profile.
              onGoPremium={() => navigate('/upgrade?plan=listener_premium')}
              onDismiss={() => setJustPlayed(null)}
            />
          ) : myRequests.requests.length > 0 ? (
            <YourSongCard requests={myRequests.requests} onSwitch={switchSong} />
          ) : (
            <PerformerCard
              band={headerBand}
              crate={bootstrap.data?.crate ?? { totalSongs: 0, artwork: [] }}
              onOpenCrate={openCrate}
              followControl={<FollowButton bandId={bandid} viewerRole={band.viewer_role} />}
            />
          )}
          {shelfCard && (
            <SkinShelfPanel
              card={shelfCard.displayName ? shelfCard : null}
              onBack={closeShelf}
              onClose={closeShelf}
              purchase={skinPurchase}
              onGetIt={buyFromShelf}
              refreshKey={shelfRefresh}
              onEquipChange={shelfEquipChange}
            />
          )}
          {/* Hidden, not unmounted, under the shelf: the chat keeps its socket. */}
          <section className="stream-chat-card" hidden={Boolean(shelfCard)}>
            <StreamActionBar onAction={handleAction} disabled={disabledActions} disabledReasons={disabledReasons} active={boostPicking ? 'boost' : tipOpen && canPay ? 'tip' : null} />
            {/* A boost that settles while the desk is closed (back from Checkout). */}
            {/* A request that settles after the panels closed (back from Checkout). */}
            {!requestFlow && (requestPayment.error || requestPayment.state === 'sending') && (
              <p className="stream-boost-notice" role="status" data-testid="stream-request-notice">
                {requestPayment.error ? t('stream.check.errors.generic') : t('stream.check.sending')}
              </p>
            )}
            {!boostPicking && (boostPayment.error || boostPayment.state === 'paying') && (
              <p className="stream-boost-notice" role="status" data-testid="stream-boost-notice">
                {boostPayment.error
                  ? t(`stream.boost.desk.payError.${boostPayError(boostPayment.error)}`)
                  : t('stream.boost.desk.paying')}
              </p>
            )}
            {/* A skin purchase that settles with nothing on (back from Checkout), and a bought skin. */}
            {!skinTryOn.tryOn && (skinPurchase.error || skinPurchase.state === 'buying') && (
              <p className="stream-boost-notice" role="status" data-testid="stream-skin-notice">
                {skinPurchase.error
                  ? skinPurchaseError(t, format, skinPurchase)
                  : t('stream.tryOn.buying')}
              </p>
            )}
            {skinNotice && !skinPurchase.error && skinPurchase.state !== 'buying' && (
              <p className="stream-boost-notice" role="status" data-testid="stream-skin-notice">
                {skinNotice.editionNumber && skinNotice.editionSize
                  ? t('stream.tryOn.boughtEdition', {
                    name: skinNotice.name || t('stream.tryOn.yourSkin'),
                    number: format.number(skinNotice.editionNumber),
                    size: format.number(skinNotice.editionSize),
                  })
                  : t('stream.tryOn.bought', { name: skinNotice.name || t('stream.tryOn.yourSkin') })}
              </p>
            )}
            <ChatSidebar
              bandId={bandid}
              isVisible
              isLive={isLive}
              variant="stream"
              ownUserId={getCurrentUserId()}
              localLines={ownChatLines}
              heldBoosts={fxBoosts.held}
              banner={canPay ? <SaveCardBanner /> : null}
              pinned={<ChatHighlights highlights={isLive ? bootstrap.data?.highlights : null} />}
              onSeeWhatTheySee={seeWhatTheySee}
              onTryOn={tryItOn}
              onSeeAll={openShelf}
              ownNamePaint={skinTryOn.tryOn?.paint ?? null}
              tryOnPanel={skinTryOn.tryOn ? (
                <SkinTryOnPanel
                  tryOn={skinTryOn.tryOn}
                  offer={skinTryOn.offer}
                  purchase={skinPurchase}
                  onGetIt={(skin, offer) => {
                    if (!isSignedIn()) {
                      goSignIn();
                      return;
                    }
                    skinPurchase.buy(skin.id, { priceCents: offer?.priceCents, name: skin.name, onSale: Boolean(offer?.sale) });
                  }}
                  onTakeOff={() => { skinTryOn.takeOff(); resetSkinPurchase(); }}
                  onOfferEnd={skinTryOn.refresh}
                />
              ) : null}
            />
          </section>
          </div>
        </aside>
      </div>

      {boostPicking && (
        <section className="stream-pick-strip" data-testid="stream-pick-strip" aria-label={t('stream.boost.pickTitle')}>
          {boostEntry ? (
            <>
              {/* The desk (mockup 06, ENG-677); another boostable song switches the target. */}
              <BoostDesk
                bandId={bandid}
                entry={boostEntry}
                queueKey={bootstrap.data?.queue}
                onConfirm={(quote) => boostPayment.pay(boostEntry, quote.amountToAdd)}
                payState={boostPayment.state}
                payError={boostPayment.error}
                showAnimation={isSignedIn()}
              />
              <SetlistRail
                entries={liveQueue}
                onSelectEntry={(entry) => { if (canBoostEntry(entry)) setBoostEntryId(entry.entryId); }}
                highlightedEntryId={boostEntry.entryId}
                yourBoosts={yourBoosts}
              />
            </>
          ) : (
            <SetlistRail
              entries={liveQueue}
              loading={bootstrap.loading}
              error={bootstrap.error}
              onBoostEntry={(entry) => setBoostEntryId(entry.entryId)}
            />
          )}
          <button type="button" className="stream-pick-cancel" onClick={closeBoostPick}>
            {t('stream.boost.cancel')}
          </button>
        </section>
      )}

      {isLive && lyricsEnabled && (
        <div className="stream-page-extras">
          <StreamLyricsPanel
            song={nowPlaying ? { Title: nowPlaying.title, Artist: nowPlaying.artist, SongId: nowPlaying.songId } : null}
            lyrics={lyrics.lyrics}
            status={lyrics.status}
            code={lyrics.code}
          />
        </div>
      )}

      <SongRecommendationNotification
        recommendation={recommendation}
        onDismiss={dismissRecommendation}
      />

      <footer className="stream-page-footer">
        <Link to="/terms" className="stream-page-footer-link">{t('footer.termsOfService')}</Link>
        <Link to="/privacy" className="stream-page-footer-link">{t('footer.privacyPolicy')}</Link>
      </footer>
    </div>
  );
};

export default StreamPage;
